import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { DashboardKpiEntry } from '@/features/dashboard/components/dashboard/dashboard-kpi-entry';
import { DashboardProjectCardHeaderContent } from '@/features/dashboard/components/dashboard/dashboard-project-card-header-content';
import { BaseProjectSlideCardProps } from '@/features/dashboard/components/dashboard/dashboard-project-slot-card';
import { getKpiMetricsSummary } from '@/features/dashboard/utils/kpi-metrics';
import { KpiWithLastTracker } from '@/features/projects/project-details/hooks/use-kpis-with-existing-entries';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';

export const DashboardProjectKpis = ({
  project,
  kpis,
  currentCardIndex,
  onCardIndexChange,
  isPaused,
  onTogglePause,
}: BaseProjectSlideCardProps & {
  kpis: KpiWithLastTracker[];
}) => {
  const { t } = useTranslation('overview', {
    keyPrefix: 'OverviewPage.kpiCards',
  });
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });
  const navigate = useNavigate();

  return (
    <Card variant="informationAlt" className="size-full">
      <CardHeader className="flex flex-row items-start justify-between">
        <DashboardProjectCardHeaderContent
          kpiName={t('latestKpis')}
          projectName={project.name}
          currentPageIndex={currentCardIndex}
          onCardIndexChange={onCardIndexChange}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-4 overflow-hidden">
        {kpis.map((kpi) => {
          const { changePercentage } = getKpiMetricsSummary(kpi.metrics);

          return (
            <div
              className="flex cursor-pointer flex-row gap-3 overflow-hidden rounded-sm bg-primary-shade-grey-500/5 p-2.5"
              key={kpi.id}
              onClick={() =>
                kpi.project_id &&
                navigate(
                  paths.insights.getHref({
                    kpiUniqueId: getKpiUniqueId(kpi),
                    projectId: kpi.project_id.toString(),
                  }),
                )
              }
            >
              <DashboardKpiEntry
                kpiName={kpi.name}
                kpiUnit={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
                changePercentage={changePercentage}
                showTendencyIndicator={true}
                logoUrl={kpi.lastTrackerOrg?.image_url ?? undefined}
                orgName={kpi.lastTrackerOrg?.name ?? undefined}
              />
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
